import React from 'react';
import {
  colorWithAlpha,
  getContrastDividerColor,
} from '../../../common/color';

function dispatchTimelineTaskInfo(detail) {
  document.dispatchEvent(new CustomEvent('timeline:open-task-info', {
    detail,
  }));
}

function findMatchingOccurrence(occurrences, startMin, endMin, timeStringToMinutes) {
  if (!Array.isArray(occurrences) || occurrences.length < 1) {
    return null;
  }

  return occurrences.find((occurrence) => {
    const occurrenceStartMin = timeStringToMinutes(occurrence.start);
    const occurrenceEndMin = timeStringToMinutes(occurrence.end);
    return endMin > occurrenceStartMin && startMin < occurrenceEndMin;
  }) ?? null;
}

function formatDuration(minutes) {
  const numericMinutes = Number(minutes);
  if (!Number.isFinite(numericMinutes) || numericMinutes <= 0) {
    return '';
  }

  const roundedMinutes = Math.round(numericMinutes);
  if (roundedMinutes < 60) {
    return `${roundedMinutes} min`;
  }

  const hours = Math.floor(roundedMinutes / 60);
  const restMinutes = roundedMinutes % 60;
  return restMinutes > 0 ? `${hours} h ${restMinutes} min` : `${hours} h`;
}

export default function TaskBlock({
  task,
  wf,
  idx,
  zoom,
  baseScale,
  timeStringToMinutes,
  timeToPixels,
  onHoverChange,
  onTimelineContextMenu,
  workflowType = 'day',
  workflowEntity = 'day-workflows',
  workflowRowId = null,
  isToday = false,
  currentMinute = null,
}) {
  const previousTask = idx > 0 ? wf?.tasks?.[idx - 1] : null;
  const startMin = timeStringToMinutes(task.start);
  const endMin = timeStringToMinutes(task.end);
  const matchingOccurrence = findMatchingOccurrence(
    wf?.occurrences,
    startMin,
    endMin,
    timeStringToMinutes,
  );
  const occurrenceStartMin = matchingOccurrence
    ? timeStringToMinutes(matchingOccurrence.start)
    : null;
  const occurrenceEndMin = matchingOccurrence
    ? timeStringToMinutes(matchingOccurrence.end)
    : null;
  const visibleStartMin = occurrenceStartMin !== null
    ? Math.max(startMin, occurrenceStartMin)
    : startMin;
  const visibleEndMin = occurrenceEndMin !== null
    ? Math.min(endMin, occurrenceEndMin)
    : endMin;
  const previousStartMin = previousTask ? timeStringToMinutes(previousTask.start) : null;
  const previousEndMin = previousTask ? timeStringToMinutes(previousTask.end) : null;
  const baseWidth = Math.max(1, (visibleEndMin - visibleStartMin) * baseScale * zoom);
  const previousWidth = (
    previousTask
    && previousStartMin !== null
    && previousEndMin !== null
  )
    ? Math.max(1, (previousEndMin - previousStartMin) * baseScale * zoom)
    : null;
  const labelWidth = String(task.name || '').length * 8 + 24;
  const isCompact = baseWidth < labelWidth;
  const isTiny = baseWidth < 56;
  const isMicro = baseWidth < 18;
  const inlineLabel = isMicro ? '' : (isTiny ? '...' : task.name);
  const workflowColor = wf.color || '#ffffff';
  const textColor = '#f4f7fb';
  const hasTinyNeighbor = isTiny || (Number.isFinite(previousWidth) && previousWidth < 56);
  const separatorColor = getContrastDividerColor(workflowColor, {
    strong: hasTinyNeighbor,
  });
  const textShadow = '0 1px 0 rgba(0, 0, 0, 0.35)';
  const hasLeadingSeparator = (
    idx > 0
    && !task.starts_previous_day
    && previousTask
    && previousEndMin !== null
    && previousEndMin >= startMin
  );
  const tooltipStart = task.starts_previous_day
    ? `Vortag ${task.actual_start ?? task.start}`
    : (task.actual_start ?? task.start);
  const tooltipEnd = task.ends_next_day
    ? `Folgetag ${task.actual_end ?? task.end}`
    : (task.actual_end ?? task.end);
  const durationLabel = formatDuration(task.duration_minutes);
  const startLabel = String(tooltipStart ?? '').trim();
  const endLabel = String(tooltipEnd ?? '').trim();
  const timeWindowLabel = startLabel && endLabel
    ? `${startLabel} - ${endLabel}`
    : (startLabel || endLabel);
  const secondaryLabel = [timeWindowLabel, durationLabel].filter(Boolean).join(' · ');
  const showSecondaryLabel = Boolean(
    secondaryLabel
    && !isCompact
    && baseWidth >= 176
  );

  const left = timeToPixels(visibleStartMin, zoom);
  const currentMinuteValue = Number(currentMinute);
  const isRunning = Boolean(
    isToday
    && Number.isFinite(currentMinuteValue)
    && currentMinuteValue >= visibleStartMin
    && currentMinuteValue < visibleEndMin
  );
  const progressRatio = isRunning && visibleEndMin > visibleStartMin
    ? Math.max(0, Math.min(1, (currentMinuteValue - visibleStartMin) / (visibleEndMin - visibleStartMin)))
    : 0;
  const isClippedStart = Boolean(
    task.starts_previous_day
    || (occurrenceStartMin !== null && visibleStartMin > startMin)
  );
  const isClippedEnd = Boolean(
    task.ends_next_day
    || (occurrenceEndMin !== null && visibleEndMin < endMin)
  );
  const startMarkerLabel = task.starts_previous_day
    ? `Start Vortag ${task.actual_start ?? task.start}`
    : `Start ${task.actual_start ?? task.start}`;
  const endMarkerLabel = task.ends_next_day
    ? `Ende Folgetag ${task.actual_end ?? task.end}`
    : `Ende ${task.actual_end ?? task.end}`;
  const startMarkerTime = task.starts_previous_day
    ? `Vortag ${task.actual_start ?? task.start}`
    : (task.actual_start ?? task.start);
  const endMarkerTime = task.ends_next_day
    ? `Folgetag ${task.actual_end ?? task.end}`
    : (task.actual_end ?? task.end);

  function openTaskInfo() {
    dispatchTimelineTaskInfo({
      source: 'timeline-task-block',
      taskId: task.task_id,
      taskName: task.name,
      workflowId: wf.workflow_id ?? wf.id,
      workflowName: wf.name,
      workflowType,
      workflowEntity,
      workflowRowId,
      durationMinutes: task.duration_minutes,
      relOffsetMinutes: task.rel_offset_minutes,
      offsetReferenceKind: task.offset_reference_kind,
      actualStart: task.actual_start ?? task.start,
      actualEnd: task.actual_end ?? task.end,
      startsPreviousDay: Boolean(task.starts_previous_day),
      endsNextDay: Boolean(task.ends_next_day),
      description: task.description ?? '',
      otherActiveTaskCount: 0,
    });
  }

  return (
    <div
      className="task-block"
      style={{
        left: `${left}px`,
        width: `${baseWidth}px`,
        color: textColor,
        '--workflow-color': workflowColor,
        '--workflow-border': colorWithAlpha(workflowColor, 0.5, 'rgba(143, 110, 46, 0.5)'),
        '--workflow-bg': colorWithAlpha(workflowColor, 0.24, 'rgba(143, 110, 46, 0.24)'),
        '--workflow-accent': colorWithAlpha(workflowColor, 0.92, 'rgba(143, 110, 46, 0.92)'),
        '--task-accent-color': colorWithAlpha(workflowColor, 0.92, 'rgba(143, 110, 46, 0.92)'),
        '--task-border-color': colorWithAlpha(workflowColor, 0.64, 'rgba(143, 110, 46, 0.64)'),
        '--task-bg-color': colorWithAlpha(workflowColor, 0.36, 'rgba(143, 110, 46, 0.36)'),
        '--task-bg-color-mid': colorWithAlpha(workflowColor, 0.24, 'rgba(143, 110, 46, 0.24)'),
        '--task-bg-color-soft': colorWithAlpha(workflowColor, 0.16, 'rgba(143, 110, 46, 0.16)'),
        '--task-bg-color-hover': colorWithAlpha(workflowColor, 0.46, 'rgba(143, 110, 46, 0.46)'),
        '--task-inner-glow-color': colorWithAlpha(workflowColor, 0.28, 'rgba(143, 110, 46, 0.28)'),
        '--task-glass-sheen-color': colorWithAlpha(workflowColor, 0.22, 'rgba(255, 255, 255, 0.1)'),
        '--task-text-shadow': textShadow,
        '--task-separator-color': separatorColor,
        '--task-progress': `${Math.round(progressRatio * 100)}%`,
      }}
      data-task-compact={isCompact ? 'true' : 'false'}
      data-task-density={isMicro ? 'micro' : (isCompact ? 'compact' : 'normal')}
      data-task-has-meta={showSecondaryLabel ? 'true' : 'false'}
      data-task-clipped-start={isClippedStart ? 'true' : 'false'}
      data-task-clipped-end={isClippedEnd ? 'true' : 'false'}
      data-has-leading-separator={hasLeadingSeparator ? 'true' : 'false'}
      data-task-running={isRunning ? 'true' : 'false'}
      data-testid="timeline-task-block"
      data-task-id={task.task_id}
      data-task-name={task.name}
      data-workflow-id={wf.workflow_id ?? wf.id}
      data-workflow-name={wf.name}
      data-workflow-type={workflowType}
      data-workflow-entity={workflowEntity}
      data-workflow-row-id={workflowRowId ?? ''}
      data-start={task.start}
      data-end={task.end}
      data-task-reveal-placement={isCompact ? 'below' : 'above'}
      data-bs-toggle="tooltip"
      data-bs-title={`${tooltipStart} - ${tooltipEnd}`}
      data-bs-placement="top"
      role="button"
      tabIndex={0}
      onMouseEnter={() => {
        onHoverChange?.({
          kind: 'task',
          taskId: task.task_id ?? `${wf.id}:${idx}`,
          workflowId: wf.id,
          color: workflowColor,
          markers: [
            {
              key: 'start',
              type: 'start',
              minutes: startMin,
              label: startMarkerLabel,
              timeText: startMarkerTime,
            },
            {
              key: 'end',
              type: 'end',
              minutes: endMin,
              label: endMarkerLabel,
              timeText: endMarkerTime,
            },
          ],
        });
      }}
      onMouseLeave={() => onHoverChange?.(null)}
      onClick={openTaskInfo}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openTaskInfo();
        }
      }}
    >
      <span className="task-block-glass-highlight" aria-hidden="true" />
      <span className="task-block-copy">
        <span className="task-block-label">{inlineLabel}</span>
        {showSecondaryLabel ? (
          <span className="task-block-meta">{secondaryLabel}</span>
        ) : null}
      </span>
      {isCompact ? (
        <span className="task-block-reveal" aria-hidden="true">
          {task.name}
        </span>
      ) : null}
    </div>
  );
}
